// https://usehooks.com/useScript/

import { useState, useEffect } from 'react';
import { useBrowser } from '../../../common/services/BrowserService';

interface ScriptArgs {
  src: string;
  skip?: boolean;
}

export const useScript = ({ src, skip }: ScriptArgs) => {
  const [status, setStatus] = useState<string>(src ? 'loading' : 'idle');
  const { document } = useBrowser();

  useEffect(() => {
    if (document) {
      if (!src || skip) {
        setStatus('idle');
        return;
      }

      let script: HTMLScriptElement | null = document.querySelector(`script[src="${src}"]`);

      if (!script) {
        script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.setAttribute('data-status', 'loading');
        document.body.appendChild(script);

        const setAttributeFromEvent = (event: Event) => {
          !!script && script.setAttribute('data-status', event.type === 'load' ? 'ready' : 'error');
        };

        script.addEventListener('load', setAttributeFromEvent);
        script.addEventListener('error', setAttributeFromEvent);
      } else {
        setStatus(script.getAttribute('data-status') || 'idle');
      }

      const setStateFromEvent = (event: Event) => {
        setStatus(event.type === 'load' ? 'ready' : 'error');
      };

      script.addEventListener('load', setStateFromEvent);
      script.addEventListener('error', setStateFromEvent);

      return () => {
        if (script) {
          script.removeEventListener('load', setStateFromEvent);
          script.removeEventListener('error', setStateFromEvent);
        }
      };
    } else {
      return () => {
        setStatus('error');
      };
    }
  }, [src, document, skip]);

  return status;
};
